import axios from 'axios'
import React, { useState } from 'react'
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	Grid,
	IconButton,
	MenuItem,
	TextField,
	Typography
} from '@material-ui/core'
import { Add, AddCircleOutline } from '@material-ui/icons'
import { makeStyles } from '@material-ui/core/styles'
import iziToast from 'izitoast'

import useIntl from 'hooks/useIntl'
import TextToolTip from 'components/TextToolTip'
import { useStoreon } from 'storeon/react'

interface SubSection {
	subSectionId: number
	subSectionName: string
}

interface Section {
	id: number
	name: string
}

interface SubSectionSelectProps {
	sectionData: Section[]
	subSectionData: SubSection[]
	setSubSectionData: React.Dispatch<React.SetStateAction<SubSection[]>>
	selectedSubSection: string
	setSelectedSubSection: React.Dispatch<React.SetStateAction<string>>
	isCustomSubSection: boolean
	setIsCustomSubSection: React.Dispatch<React.SetStateAction<boolean>>
	customSubSection: string
	setCustomSubSection: React.Dispatch<React.SetStateAction<string>>
	refetchSubSections: (sectionId: string) => void
}

const useStyles = makeStyles(theme => ({
	iconButton: {
		backgroundColor: '#7D6EF1',
		color: 'white',
		width: 40,
		height: 40,
		borderRadius: '50%',
		padding: 4,
		marginLeft: theme.spacing(0.5)
	}
}))

const Bulksubsection: React.FC<SubSectionSelectProps> = ({
	sectionData,
	subSectionData,
	setSubSectionData,
	selectedSubSection,
	setSelectedSubSection,
	isCustomSubSection,
	setIsCustomSubSection,
	customSubSection,
	setCustomSubSection,
	refetchSubSections
}) => {
	const classes = useStyles()
	const [open, setOpen] = useState(false)
	const [newSubSection, setNewSubSection] = useState('')
	const [description, setDescription] = useState('')
	const [selectedSection, setSelectedSection] = useState<string>('')
	const { token } = useStoreon('token')
	const intl = useIntl()
	const handleOpenModal = () => {
		setNewSubSection('')
		setSelectedSection('')
		setDescription('')
		setOpen(true)
	}

	const handleCloseModal = () => {
		setOpen(false)
	}

	const handleCreate = async () => {
		const userId = localStorage.getItem('userId')

		try {
			const response = await axios.post(
				`${process.env.REACT_APP_API_URL}/api/v1/custom/createSubSection`,
				{
					name: newSubSection,
					sectionId: selectedSection,
					description: description,
					createdBy: userId
				},
				{
					headers: {
						Authorization: ` ${token}`
					}
				}
			)

			// console.log('Response:', response)

			const { data } = response

			if (data?.message) {
				iziToast.success({
					message: data.message
				})
				refetchSubSections(selectedSection)
				setOpen(false)
				handleCloseModal()
			} else {
				iziToast.error({
					message: 'Invalid response structure.'
				})
			}
		} catch (error: any) {
			console.error('Catch block error:', error)

			iziToast.error({
				message: error?.response?.data?.message || 'Something went wrong!'
			})
		}
	}

	return (
		<>
			<Grid>
				<Grid item xs={12}>
					<Typography gutterBottom>
						{intl.formatMessage({ id: 'Sub-Section Name' })}
					</Typography>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
						<FormControl fullWidth>
							<TextField
								select
								fullWidth
								value={selectedSubSection}
								onChange={e => setSelectedSubSection(e.target.value)}
								SelectProps={{
									MenuProps: {
										getContentAnchorEl: null,
										anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
										transformOrigin: { vertical: 'top', horizontal: 'left' },
										PaperProps: {
											style: { maxHeight: 200, overflowY: 'auto' }
										}
									},
									displayEmpty: true
								}}
							>
								<MenuItem value="" disabled>
									Select a Sub-Section
								</MenuItem>
								{subSectionData.length > 0 ? (
									subSectionData.map(sub => (
										<MenuItem key={sub.subSectionId} value={sub.subSectionId}>
											{sub.subSectionName}
										</MenuItem>
									))
								) : (
									<MenuItem disabled>No SubSections Available</MenuItem>
								)}
							</TextField>
						</FormControl>
						<TextToolTip title="Add Sub Section">
							<Add onClick={handleOpenModal} sx={{ color: '#7D6EF1', cursor: 'pointer' }} />
						</TextToolTip>
					</Box>
				</Grid>
			</Grid>

			<Dialog open={open} onClose={handleCloseModal} fullWidth maxWidth="sm">
				<DialogTitle>{intl.formatMessage({ id: 'Add New SubSection' })}</DialogTitle>
				<DialogContent>
					<Grid container spacing={2}>
						<Grid item xs={12}>
							<FormControl fullWidth>
								<TextField
									fullWidth
									select
									label={intl.formatMessage({ id: 'Section Name' })}
									value={selectedSection}
									onChange={e => setSelectedSection(e.target.value)}
									SelectProps={{
										MenuProps: {
											getContentAnchorEl: null,
											anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
											transformOrigin: { vertical: 'top', horizontal: 'left' },
											PaperProps: {
												style: { maxHeight: 200, overflowY: 'auto' }
											}
										},
										displayEmpty: true
									}}
								>
									<MenuItem value="" disabled>
										Select a Section
									</MenuItem>
									{Array.isArray(sectionData) && sectionData.length > 0 ? (
										sectionData
											.filter(
												section => section && section.id !== undefined && section.name
											)
											.map(section => (
												<MenuItem key={section.id} value={section.id.toString()}>
													{section.name}
												</MenuItem>
											))
									) : (
										<MenuItem disabled>No Sections Available</MenuItem>
									)}
								</TextField>
							</FormControl>
						</Grid>
						<Grid item xs={12}>
							<TextField
								fullWidth
								label={intl.formatMessage({ id: 'Sub-Section Name' })}
								placeholder="Enter subsection name"
								value={newSubSection}
								onChange={e => setNewSubSection(e.target.value)}
							/>
						</Grid>
						<Grid item xs={12}>
							<TextField
								fullWidth
								label={intl.formatMessage({ id: 'Description' })}
								multiline
								rows={3}
								value={description}
								onChange={e => setDescription(e.target.value)}
							/>
						</Grid>
					</Grid>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCloseModal} color="secondary">
						{intl.formatMessage({ id: 'Cancel' })}
					</Button>
					<Button onClick={handleCreate} color="primary" variant="contained">
						{intl.formatMessage({ id: 'Create' })}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	)
}

export default Bulksubsection
